/**
 * FormBuilder Component
 * Visual form builder with drag-and-drop grid canvas layout using Fluent UI.
 * Left panel: field type palette + selected field editor.
 * Main area: GridCanvas for drag/resize placement.
 */

import * as React from 'react';
import {
  Card,
  CardHeader,
  CardPreview,
  Button,
  Input,
  Textarea,
  Text,
  Divider,
  Switch,
  Field,
  Tooltip,
  Badge,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import {
  AddRegular,
  DeleteRegular,
  DismissRegular,
  TextFieldRegular,
  MailRegular,
  PhoneRegular,
  CalendarRegular,
  CalendarClockRegular,
  ClockRegular,
  NumberSymbolRegular,
  ChevronDownRegular,
  CheckboxCheckedRegular,
  RadioButtonRegular,
  TextAlignLeftRegular,
  AttachRegular,
  DrawTextRegular,
  MathFormulaRegular,
  BorderNoneRegular,
  QrCodeRegular,
  ImageRegular,
  TableRegular,
} from '@fluentui/react-icons';
import type {
  FieldDefinition,
  FieldType,
  SubFieldType,
  SubFieldDefinition,
  FormTab,
  FormSection,
  SelectOption,
  RequestTemplate,
  CreateRequestDto,
  FieldMapping,
} from '@structured-request/core';
import {
  FIELD_TYPE_INFO,
  generateId,
  toCamelCase,
  createFieldDefinition,
} from '@structured-request/core';
import { GridCanvas } from './GridCanvas.js';
import type { GridLayoutUpdate } from './GridCanvas.js';

const useStyles = makeStyles({
  container: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  headerFields: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  settingsRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalL,
    flexWrap: 'wrap',
    alignItems: 'center',
  },
  builderLayout: {
    display: 'flex',
    gap: tokens.spacingHorizontalL,
    alignItems: 'flex-start',
  },
  leftPanel: {
    width: '280px',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  mainPanel: {
    flex: '1 1 0',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  rightPanel: {
    width: '300px',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
    overflowY: 'auto' as const,
    maxHeight: '80vh',
    position: 'sticky' as const,
    top: tokens.spacingVerticalM,
  },
  fieldTypeGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: tokens.spacingVerticalXS,
  },
  fieldTypeButton: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    justifyContent: 'flex-start',
    padding: tokens.spacingVerticalXS + ' ' + tokens.spacingHorizontalS,
  },
  editPanel: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  editPanelGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: tokens.spacingVerticalS,
  },
  sectionList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  sectionRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    alignItems: 'center',
  },
  optionsList: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
  },
  optionRow: {
    display: 'flex',
    gap: tokens.spacingHorizontalXS,
    alignItems: 'center',
  },
  toolbar: {
    display: 'flex',
    gap: tokens.spacingHorizontalS,
    alignItems: 'center',
    flexWrap: 'wrap',
  },
});

const FIELD_ICONS: Record<FieldType, React.ReactElement> = {
  text: React.createElement(TextFieldRegular),
  textarea: React.createElement(TextAlignLeftRegular),
  number: React.createElement(NumberSymbolRegular),
  email: React.createElement(MailRegular),
  phone: React.createElement(PhoneRegular),
  date: React.createElement(CalendarRegular),
  datetime: React.createElement(CalendarClockRegular),
  time: React.createElement(ClockRegular),
  select: React.createElement(ChevronDownRegular),
  multiselect: React.createElement(CheckboxCheckedRegular),
  checkbox: React.createElement(CheckboxCheckedRegular),
  radio: React.createElement(RadioButtonRegular),
  file: React.createElement(AttachRegular),
  scan: React.createElement(QrCodeRegular),
  photo: React.createElement(ImageRegular),
  signature: React.createElement(DrawTextRegular),
  computed: React.createElement(MathFormulaRegular),
  spacer: React.createElement(BorderNoneRegular),
  repeater: React.createElement(TableRegular),
};

export interface FormBuilderProps {
  template?: Partial<RequestTemplate> | null;
  onChange: (template: Partial<CreateRequestDto>) => void;
  onSave?: () => void;
  onCancel?: () => void;
  isSaving?: boolean;
  disabled?: boolean;
  showActions?: boolean;
}

export const FormBuilder: React.FC<FormBuilderProps> = ({
  template,
  onChange,
  onSave,
  onCancel,
  isSaving = false,
  disabled = false,
  showActions = true,
}) => {
  const styles = useStyles();
  const [selectedFieldId, setSelectedFieldId] = React.useState<string | null>(null);
  const [activeTabId, setActiveTabId] = React.useState<string | null>(null);
  const [isGridMode, setIsGridMode] = React.useState(true);

  const [showAdvanced, setShowAdvanced] = React.useState(false);

  const title = template?.title ?? '';
  const description = template?.description ?? '';
  const instructions = template?.instructions ?? '';
  const fields = template?.fields ?? [];
  const oneTimeUse = template?.oneTimeUse ?? false;
  const requiresAuth = template?.requiresAuth ?? false;
  const gridColumns = template?.gridColumns ?? 1;
  const primaryLogoUrl = template?.primaryLogoUrl ?? '';
  const secondaryLogoUrl = template?.secondaryLogoUrl ?? '';
  const webhookUrl = template?.webhookUrl ?? '';
  const notifyEmail = template?.notifyEmail ?? '';
  const responseEditMode = template?.responseEditMode ?? 'readonly';
  const showResponseUrl = template?.showResponseUrl ?? false;
  const externalIdParam = template?.externalIdParam ?? '';
  const fieldMappings: FieldMapping[] = template?.fieldMappings ?? [];
  const tabs: FormTab[] = template?.tabs ?? [];
  const sections: FormSection[] = template?.sections ?? [];
  const sortedTabs = [...tabs].sort((a, b) => a.order - b.order);

  React.useEffect(() => {
    if (tabs.length > 0 && !activeTabId) {
      setActiveTabId(tabs[0].id);
    }
    if (tabs.length === 0) {
      setActiveTabId(null);
    }
  }, [tabs.length]);

  const updateTemplate = (updates: Partial<CreateRequestDto>) => {
    onChange({ ...template, ...updates });
  };

  const updateField = (fieldId: string, updates: Partial<FieldDefinition>) => {
    const updatedFields = fields.map((f) =>
      f.id === fieldId ? { ...f, ...updates } : f
    );
    updateTemplate({ fields: updatedFields });
  };

  const addField = (type: FieldType) => {
    const info = FIELD_TYPE_INFO[type];
    const newField = createFieldDefinition({
      type,
      label: 'New ' + info.label,
      order: fields.length,
      options: info.hasOptions ? [{ value: 'option1', label: 'Option 1' }] : undefined,
      tabId: activeTabId ?? undefined,
      gridX: 0,
      gridY: fields.length * 3,
      gridW: 1,
      gridH: 2,
    });
    updateTemplate({ fields: [...fields, newField] });
    setSelectedFieldId(newField.id);
  };

  const removeField = (fieldId: string) => {
    const updatedFields = fields
      .filter((f) => f.id !== fieldId)
      .map((f, idx) => ({ ...f, order: idx }));
    updateTemplate({ fields: updatedFields });
    if (selectedFieldId === fieldId) {
      setSelectedFieldId(null);
    }
  };

  const duplicateField = (field: FieldDefinition) => {
    const newField: FieldDefinition = {
      ...field,
      id: generateId('fld'),
      name: field.name + 'Copy',
      label: field.label + ' (Copy)',
      order: fields.length,
      gridY: (field.gridY ?? 0) + (field.gridH ?? 2),
    };
    updateTemplate({ fields: [...fields, newField] });
  };

  const handleLayoutChange = (updates: GridLayoutUpdate[]) => {
    const updatedFields = fields.map((f) => {
      const upd = updates.find((u) => u.id === f.id);
      if (!upd) return f;
      return { ...f, gridX: upd.gridX, gridY: upd.gridY, gridW: upd.gridW, gridH: upd.gridH };
    });
    updateTemplate({ fields: updatedFields });
  };

  const addOption = (fieldId: string) => {
    const field = fields.find((f) => f.id === fieldId);
    if (!field) return;
    const options = field.options ?? [];
    const newOpt: SelectOption = {
      value: 'option' + (options.length + 1),
      label: 'Option ' + (options.length + 1),
    };
    updateField(fieldId, { options: [...options, newOpt] });
  };

  const updateOption = (fieldId: string, idx: number, updates: Partial<SelectOption>) => {
    const field = fields.find((f) => f.id === fieldId);
    if (!field?.options) return;
    updateField(fieldId, {
      options: field.options.map((o, i) => (i === idx ? { ...o, ...updates } : o)),
    });
  };

  const removeOption = (fieldId: string, idx: number) => {
    const field = fields.find((f) => f.id === fieldId);
    if (!field?.options) return;
    updateField(fieldId, { options: field.options.filter((_, i) => i !== idx) });
  };

  const addTab = () => {
    const newTab: FormTab = {
      id: generateId('tab'),
      label: 'Tab ' + (tabs.length + 1),
      order: tabs.length,
    };
    const newTabs = [...tabs, newTab];
    updateTemplate({ tabs: newTabs });
    setActiveTabId(newTab.id);
  };

  const renameTab = (tabId: string, label: string) => {
    updateTemplate({ tabs: tabs.map((t) => (t.id === tabId ? { ...t, label } : t)) });
  };

  const deleteTab = (tabId: string) => {
    const remaining = tabs.filter((t) => t.id !== tabId).map((t, i) => ({ ...t, order: i }));
    const fallbackTabId = remaining[0]?.id;
    const updatedFields = fields.map((f) =>
      f.tabId === tabId ? { ...f, tabId: fallbackTabId } : f
    );
    updateTemplate({ tabs: remaining, fields: updatedFields });
    if (activeTabId === tabId) setActiveTabId(remaining[0]?.id ?? null);
  };

  const addSection = () => {
    const newSection: FormSection = {
      id: generateId('sec'),
      label: 'Section ' + (sections.length + 1),
      showTitle: true,
      tabId: activeTabId ?? undefined,
      order: sections.length,
    };
    updateTemplate({ sections: [...sections, newSection] });
  };

  const renameSection = (sectionId: string, label: string) => {
    updateTemplate({
      sections: sections.map((s) => (s.id === sectionId ? { ...s, label } : s)),
    });
  };

  const toggleSectionTitle = (sectionId: string) => {
    updateTemplate({
      sections: sections.map((s) =>
        s.id === sectionId ? { ...s, showTitle: !s.showTitle } : s
      ),
    });
  };

  const deleteSection = (sectionId: string) => {
    const updatedFields = fields.map((f) =>
      f.sectionId === sectionId ? { ...f, sectionId: undefined } : f
    );
    updateTemplate({
      sections: sections.filter((s) => s.id !== sectionId),
      fields: updatedFields,
    });
  };

  const canvasFields = fields.filter((f) => {
    if (sortedTabs.length === 0) return true;
    const firstTabId = sortedTabs[0]?.id;
    return f.tabId ? f.tabId === activeTabId : activeTabId === firstTabId;
  });

  const selectedField = fields.find((f) => f.id === selectedFieldId) ?? null;
  const hasOptionsType = selectedField ? FIELD_TYPE_INFO[selectedField.type]?.hasOptions : false;
  const currentSections = sections.filter((s) =>
    s.tabId ? s.tabId === activeTabId : true
  );

  const nativeSelectStyle: React.CSSProperties = {
    padding: tokens.spacingVerticalXS + ' ' + tokens.spacingHorizontalS,
    borderRadius: tokens.borderRadiusSmall,
    border: '1px solid ' + tokens.colorNeutralStroke1,
    background: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
  };

  return (
    <div className={styles.container}>
      <Card>
        <CardPreview style={{ padding: tokens.spacingVerticalL }}>
          <div className={styles.headerFields}>
            <Field label="Request Name" required>
              <Input
                value={title}
                onChange={(_, d) => updateTemplate({ title: d.value })}
                placeholder="e.g., Client Onboarding Information"
                disabled={disabled}
              />
            </Field>
            <Field label="Description">
              <Textarea
                value={description}
                onChange={(_, d) => updateTemplate({ description: d.value })}
                placeholder="Brief description shown to respondents"
                disabled={disabled}
                rows={2}
              />
            </Field>
            <Field label="Instructions">
              <Textarea
                value={instructions}
                onChange={(_, d) => updateTemplate({ instructions: d.value })}
                placeholder="Detailed instructions for completing this form"
                disabled={disabled}
                rows={2}
              />
            </Field>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: tokens.spacingHorizontalM }}>
              <Field label="Primary Logo URL" hint="MSP / your brand logo">
                <Input
                  value={primaryLogoUrl}
                  onChange={(_, d) => updateTemplate({ primaryLogoUrl: d.value || undefined })}
                  placeholder="https://example.com/logo.png"
                  disabled={disabled}
                  type="url"
                />
              </Field>
              <Field label="Secondary Logo URL" hint="Client logo">
                <Input
                  value={secondaryLogoUrl}
                  onChange={(_, d) => updateTemplate({ secondaryLogoUrl: d.value || undefined })}
                  placeholder="https://client.com/logo.png"
                  disabled={disabled}
                  type="url"
                />
              </Field>
            </div>
            <div className={styles.settingsRow}>
              <Switch
                checked={oneTimeUse}
                onChange={(_, d) => updateTemplate({ oneTimeUse: d.checked })}
                label="One-time use"
                disabled={disabled}
              />
              <Switch
                checked={requiresAuth}
                onChange={(_, d) => updateTemplate({ requiresAuth: d.checked })}
                label="Require authentication"
                disabled={disabled}
              />
              <Field label="Grid Columns">
                <div style={{ display: 'flex', gap: tokens.spacingHorizontalXS }}>
                  {[1, 2, 3, 4].map((n) => (
                    <Button
                      key={n}
                      appearance={gridColumns === n ? 'primary' : 'secondary'}
                      size="small"
                      disabled={disabled}
                      onClick={() => updateTemplate({ gridColumns: n })}
                    >
                      {String(n)}
                    </Button>
                  ))}
                </div>
              </Field>
            </div>
          </div>
        </CardPreview>
      </Card>

      {/* Tabs toolbar */}
      <div className={styles.toolbar}>
        {sortedTabs.map((tab) => (
          <div key={tab.id} style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalXS }}>
            <Button
              appearance={activeTabId === tab.id ? 'primary' : 'secondary'}
              size="small"
              onClick={() => setActiveTabId(tab.id)}
            >
              {tab.label}
            </Button>
            {tabs.length > 0 && (
              <Tooltip content="Rename tab" relationship="label">
                <Input
                  value={tab.label}
                  onChange={(_, d) => renameTab(tab.id, d.value)}
                  disabled={disabled}
                  size="small"
                  style={{ width: '80px' }}
                />
              </Tooltip>
            )}
            <Button
              icon={React.createElement(DismissRegular)}
              appearance="subtle"
              size="small"
              disabled={disabled}
              onClick={() => deleteTab(tab.id)}
            />
          </div>
        ))}
        <Button
          icon={React.createElement(AddRegular)}
          appearance="subtle"
          size="small"
          disabled={disabled}
          onClick={addTab}
        >
          Add Tab
        </Button>
      </div>

      {/* Builder 2-panel layout */}
      <div className={styles.builderLayout}>
        {/* Left panel */}
        <Card className={styles.leftPanel}>
          <CardHeader header={<Text size={300} weight="semibold">Add Field</Text>} />
          <CardPreview style={{ padding: '0 ' + tokens.spacingHorizontalM + ' ' + tokens.spacingVerticalM }}>
            <div className={styles.fieldTypeGrid}>
              {(Object.keys(FIELD_TYPE_INFO) as FieldType[]).map((type) => {
                const info = FIELD_TYPE_INFO[type];
                return (
                  <Tooltip key={type} content={info.description} relationship="description">
                    <Button
                      appearance="subtle"
                      size="small"
                      disabled={disabled}
                      icon={FIELD_ICONS[type]}
                      onClick={() => addField(type)}
                      style={{ justifyContent: 'flex-start' }}
                    >
                      {info.label}
                    </Button>
                  </Tooltip>
                );
              })}
            </div>

            <Divider style={{ margin: tokens.spacingVerticalM + ' 0' }} />

            <Text size={200} weight="semibold">Sections</Text>
            <div className={styles.sectionList} style={{ marginTop: tokens.spacingVerticalXS }}>
              {currentSections.length === 0 ? (
                <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>No sections</Text>
              ) : (
                currentSections.map((sec) => (
                  <div key={sec.id} className={styles.sectionRow}>
                    <Input
                      value={sec.label}
                      onChange={(_, d) => renameSection(sec.id, d.value)}
                      disabled={disabled}
                      size="small"
                      style={{ flex: 1 }}
                    />
                    <Tooltip content={sec.showTitle ? 'Hide title' : 'Show title'} relationship="label">
                      <button
                        onClick={() => toggleSectionTitle(sec.id)}
                        style={{ cursor: 'pointer', background: 'none', border: 'none', padding: 0 }}
                      >
                        <Badge
                          appearance={sec.showTitle ? 'filled' : 'outline'}
                          color="informative"
                        >
                          T
                        </Badge>
                      </button>
                    </Tooltip>
                    <Button
                      icon={React.createElement(DeleteRegular)}
                      appearance="subtle"
                      size="small"
                      disabled={disabled}
                      onClick={() => deleteSection(sec.id)}
                    />
                  </div>
                ))
              )}
              <Button
                icon={React.createElement(AddRegular)}
                appearance="subtle"
                size="small"
                disabled={disabled}
                onClick={addSection}
                style={{ alignSelf: 'flex-start', marginTop: tokens.spacingVerticalXS }}
              >
                Add Section
              </Button>
            </div>
          </CardPreview>
        </Card>

        {/* Main panel */}
        <div className={styles.mainPanel}>
          <div className={styles.toolbar}>
            <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
              {canvasFields.length} field(s)
            </Text>
            <Switch
              checked={isGridMode}
              onChange={(_, d) => setIsGridMode(d.checked)}
              label={isGridMode ? 'Drag mode on' : 'Drag mode off'}
              disabled={disabled}
            />
          </div>

          <GridCanvas
            fields={canvasFields}
            gridColumns={gridColumns}
            isEditing={isGridMode && !disabled}
            selectedFieldId={selectedFieldId}
            onLayoutChange={handleLayoutChange}
            onFieldSelect={setSelectedFieldId}
            onFieldDelete={removeField}
          />
        </div>

        {/* Right panel — field editor */}
        <div className={styles.rightPanel}>
          {selectedField && (
            <Card>
              <CardHeader
                header={
                  <div style={{ display: 'flex', alignItems: 'center', gap: tokens.spacingHorizontalS }}>
                    {FIELD_ICONS[selectedField.type]}
                    <Text size={300} weight="semibold">Edit: {selectedField.label}</Text>
                  </div>
                }
                action={
                  <Button
                    icon={React.createElement(DismissRegular)}
                    appearance="subtle"
                    size="small"
                    onClick={() => setSelectedFieldId(null)}
                  />
                }
              />
              <CardPreview style={{ padding: tokens.spacingVerticalM }}>
                <div className={styles.editPanel}>
                  <div className={styles.editPanelGrid}>
                    <Field label="Label">
                      <Input
                        value={selectedField.label}
                        onChange={(_, d) => {
                          updateField(selectedField.id, {
                            label: d.value,
                            name: toCamelCase(d.value),
                          });
                        }}
                        disabled={disabled}
                      />
                    </Field>
                    <Field label="Field name (code)">
                      <Input
                        value={selectedField.name}
                        onChange={(_, d) => updateField(selectedField.id, { name: d.value })}
                        disabled={disabled}
                      />
                    </Field>
                    <Field label="Placeholder">
                      <Input
                        value={selectedField.placeholder ?? ''}
                        onChange={(_, d) =>
                          updateField(selectedField.id, { placeholder: d.value })
                        }
                        disabled={disabled}
                      />
                    </Field>
                    <Field label="Help Text">
                      <Input
                        value={selectedField.description ?? ''}
                        onChange={(_, d) =>
                          updateField(selectedField.id, { description: d.value })
                        }
                        disabled={disabled}
                      />
                    </Field>
                  </div>

                  <div className={styles.settingsRow}>
                    <Switch
                      checked={selectedField.required}
                      onChange={(_, d) =>
                        updateField(selectedField.id, { required: d.checked })
                      }
                      label="Required"
                      disabled={disabled}
                    />
                    <Switch
                      checked={selectedField.isSecure ?? false}
                      onChange={(_, d) =>
                        updateField(selectedField.id, { isSecure: d.checked })
                      }
                      label="Secure"
                      disabled={disabled}
                    />
                    {selectedField.type !== 'file' &&
                      selectedField.type !== 'computed' &&
                      selectedField.type !== 'spacer' && (
                      <Tooltip
                        content="Pre-fill this field with the last value entered by the user (stored in browser only — nothing is sent to the server)"
                        relationship="description"
                      >
                        <Switch
                          checked={selectedField.enableStorage ?? false}
                          onChange={(_, d) =>
                            updateField(selectedField.id, { enableStorage: d.checked })
                          }
                          label="Remember value"
                          disabled={disabled || (selectedField.isSecure ?? false)}
                        />
                      </Tooltip>
                    )}
                  </div>

                  <Field
                    label="Show this field only when"
                    hint={'Use {fieldName} with a comparison, e.g. {provisionDialpad} === true  or  {employeeType} === \'contractor\'  —  leave blank to always show'}
                  >
                    <Input
                      value={selectedField.showWhen ?? ''}
                      onChange={(_, d) =>
                        updateField(selectedField.id, { showWhen: d.value || undefined })
                      }
                      placeholder="{fieldName} === 'value'"
                      disabled={disabled}
                    />
                  </Field>

                  <div className={styles.settingsRow} style={{ flexDirection: 'column', alignItems: 'flex-start' }}>
                    <Field label="Width (cols)">
                      <div style={{ display: 'flex', gap: tokens.spacingHorizontalXS }}>
                        {Array.from({ length: gridColumns }, (_, i) => i + 1).map((n) => (
                          <Button
                            key={n}
                            appearance={(selectedField.gridW ?? 1) === n ? 'primary' : 'secondary'}
                            size="small"
                            disabled={disabled}
                            onClick={() => updateField(selectedField.id, { gridW: n })}
                          >
                            {String(n)}
                          </Button>
                        ))}
                      </div>
                    </Field>
                    <Field label="Height (rows)">
                      <div style={{ display: 'flex', gap: tokens.spacingHorizontalXS }}>
                        {[1, 2, 3, 4].map((n) => (
                          <Button
                            key={n}
                            appearance={(selectedField.gridH ?? 2) === n ? 'primary' : 'secondary'}
                            size="small"
                            disabled={disabled}
                            onClick={() => updateField(selectedField.id, { gridH: n })}
                          >
                            {String(n)}
                          </Button>
                        ))}
                      </div>
                    </Field>
                  </div>

                  {sortedTabs.length > 0 && (
                    <Field label="Tab">
                      <select
                        value={selectedField.tabId ?? sortedTabs[0].id}
                        onChange={(e) => {
                          updateField(selectedField.id, { tabId: e.target.value });
                          setSelectedFieldId(null);
                        }}
                        disabled={disabled}
                        style={nativeSelectStyle}
                      >
                        {sortedTabs.map((tab) => (
                          <option key={tab.id} value={tab.id}>{tab.label}</option>
                        ))}
                      </select>
                    </Field>
                  )}

                  {sections.length > 0 && (
                    <Field label="Section">
                      <select
                        value={selectedField.sectionId ?? ''}
                        onChange={(e) =>
                          updateField(selectedField.id, {
                            sectionId: e.target.value || undefined,
                          })
                        }
                        disabled={disabled}
                        style={nativeSelectStyle}
                      >
                        <option value="">— None —</option>
                        {sections.map((s) => (
                          <option key={s.id} value={s.id}>{s.label}</option>
                        ))}
                      </select>
                    </Field>
                  )}

                  {hasOptionsType && (
                    <div className={styles.optionsList}>
                      <Text weight="semibold" size={200}>Options</Text>
                      {(selectedField.options ?? []).map((opt, optIdx) => (
                        <div key={optIdx} className={styles.optionRow}>
                          <Input
                            value={opt.label}
                            onChange={(_, d) =>
                              updateOption(selectedField.id, optIdx, {
                                label: d.value,
                                value: toCamelCase(d.value),
                              })
                            }
                            placeholder="Option label"
                            disabled={disabled}
                            style={{ flex: 1 }}
                            size="small"
                          />
                          <Button
                            icon={React.createElement(DismissRegular)}
                            appearance="subtle"
                            size="small"
                            disabled={disabled || (selectedField.options?.length ?? 0) <= 1}
                            onClick={() => removeOption(selectedField.id, optIdx)}
                          />
                        </div>
                      ))}
                      <Button
                        icon={React.createElement(AddRegular)}
                        appearance="subtle"
                        size="small"
                        disabled={disabled}
                        onClick={() => addOption(selectedField.id)}
                      >
                        Add Option
                      </Button>
                    </div>
                  )}

                  {selectedField.type === 'computed' && (
                    <>
                      <Divider style={{ margin: `${tokens.spacingVerticalS} 0` }} />
                      <Text weight="semibold" size={200}>Computed Formula</Text>
                      <Field
                        label="Template"
                        hint={'Use {fieldName} placeholders, e.g. {firstName}.{lastName}@example.com'}
                      >
                        <Input
                          value={selectedField.computedTemplate ?? ''}
                          onChange={(_, d) =>
                            updateField(selectedField.id, { computedTemplate: d.value })
                          }
                          placeholder="{firstName}.{lastName}@example.com"
                          disabled={disabled}
                          contentBefore={React.createElement(MathFormulaRegular, { style: { color: tokens.colorNeutralForeground3 } })}
                        />
                      </Field>
                      <div style={{ marginTop: tokens.spacingVerticalXS }}>
                        <Text size={200} style={{ color: tokens.colorNeutralForeground3 }}>
                          Available fields:{' '}
                          {fields
                            .filter((f) => f.id !== selectedField.id && f.name)
                            .map((f) => `{${f.name}}`)
                            .join(', ') || 'none yet'}
                        </Text>
                      </div>
                      <Switch
                        checked={selectedField.allowComputedOverride ?? false}
                        onChange={(_, d) =>
                          updateField(selectedField.id, { allowComputedOverride: d.checked })
                        }
                        label="Allow user to override computed value"
                        disabled={disabled}
                      />
                      {selectedField.allowComputedOverride && (
                        <Field
                          label="Override warning message"
                          hint="Displayed in a confirmation dialog before the user can edit this value. Leave blank to allow override without a warning."
                        >
                          <Textarea
                            value={selectedField.computedOverrideWarning ?? ''}
                            onChange={(_, d) =>
                              updateField(selectedField.id, { computedOverrideWarning: d.value })
                            }
                            placeholder="Customizing this value will lead to delays as the request will be submitted for manual review..."
                            disabled={disabled}
                            rows={4}
                            resize="vertical"
                          />
                        </Field>
                      )}
                    </>
                  )}

                  {selectedField.type === 'repeater' && (
                    <>
                      <Divider style={{ margin: `${tokens.spacingVerticalS} 0` }} />
                      <Text weight="semibold" size={200}>Columns</Text>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS }}>
                        {(selectedField.subFields ?? []).map((sf: SubFieldDefinition, sfIdx: number) => (
                          <div key={sfIdx} style={{ display: 'flex', gap: tokens.spacingHorizontalXS, alignItems: 'flex-start' }}>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS, flex: 1 }}>
                              <Input
                                value={sf.label}
                                onChange={(_, d) => {
                                  const updated = (selectedField.subFields ?? []).map((s: SubFieldDefinition, i: number) =>
                                    i === sfIdx ? { ...s, label: d.value, name: toCamelCase(d.value) } : s
                                  );
                                  updateField(selectedField.id, { subFields: updated });
                                }}
                                placeholder="Column label"
                                disabled={disabled}
                                size="small"
                              />
                              <div style={{ display: 'flex', gap: tokens.spacingHorizontalXS, alignItems: 'center' }}>
                                <select
                                  value={sf.type}
                                  onChange={(e) => {
                                    const updated = (selectedField.subFields ?? []).map((s: SubFieldDefinition, i: number) =>
                                      i === sfIdx ? { ...s, type: e.target.value as SubFieldType } : s
                                    );
                                    updateField(selectedField.id, { subFields: updated });
                                  }}
                                  disabled={disabled}
                                  style={{ ...nativeSelectStyle, fontSize: '11px', flex: 1 }}
                                >
                                  {(['text','email','phone','number','date','select','checkbox','radio','textarea'] as SubFieldType[]).map((t) => (
                                    <option key={t} value={t}>{t}</option>
                                  ))}
                                </select>
                                <Tooltip content="Required" relationship="label">
                                  <input
                                    type="checkbox"
                                    checked={sf.required ?? false}
                                    onChange={(e) => {
                                      const updated = (selectedField.subFields ?? []).map((s: SubFieldDefinition, i: number) =>
                                        i === sfIdx ? { ...s, required: e.target.checked } : s
                                      );
                                      updateField(selectedField.id, { subFields: updated });
                                    }}
                                    disabled={disabled}
                                    title="Required"
                                  />
                                </Tooltip>
                              </div>
                              {(sf.type === 'select' || sf.type === 'radio') && (
                                <Input
                                  value={(sf.options ?? []).map((o: { value: string; label: string }) => o.label).join(', ')}
                                  onChange={(_, d) => {
                                    const opts = d.value.split(',').map((s) => ({ value: toCamelCase(s.trim()), label: s.trim() })).filter((o) => o.label);
                                    const updated = (selectedField.subFields ?? []).map((s: SubFieldDefinition, i: number) =>
                                      i === sfIdx ? { ...s, options: opts } : s
                                    );
                                    updateField(selectedField.id, { subFields: updated });
                                  }}
                                  placeholder="Option 1, Option 2, Option 3"
                                  disabled={disabled}
                                  size="small"
                                />
                              )}
                            </div>
                            <Button
                              icon={React.createElement(DeleteRegular)}
                              appearance="subtle"
                              size="small"
                              disabled={disabled}
                              onClick={() => {
                                const updated = (selectedField.subFields ?? []).filter((_: SubFieldDefinition, i: number) => i !== sfIdx);
                                updateField(selectedField.id, { subFields: updated });
                              }}
                            />
                          </div>
                        ))}
                        <Button
                          icon={React.createElement(AddRegular)}
                          appearance="subtle"
                          size="small"
                          disabled={disabled}
                          onClick={() => {
                            const newSub: SubFieldDefinition = { name: `col${(selectedField.subFields?.length ?? 0) + 1}`, label: 'New Column', type: 'text' };
                            updateField(selectedField.id, { subFields: [...(selectedField.subFields ?? []), newSub] });
                          }}
                        >
                          Add Column
                        </Button>
                      </div>
                    </>
                  )}

                  <div style={{ display: 'flex', gap: tokens.spacingHorizontalS, justifyContent: 'flex-end' }}>
                    <Button
                      appearance="subtle"
                      size="small"
                      disabled={disabled}
                      onClick={() => duplicateField(selectedField)}
                    >
                      Duplicate
                    </Button>
                    <Button
                      icon={React.createElement(DeleteRegular)}
                      appearance="subtle"
                      size="small"
                      disabled={disabled}
                      onClick={() => removeField(selectedField.id)}
                    >
                      Delete
                    </Button>
                  </div>
                </div>
              </CardPreview>
            </Card>
          )}
        </div>
      </div>

      {/* Advanced Settings */}
      <Card>
        <CardHeader
          header={<Text size={300} weight="semibold">Advanced Settings</Text>}
          action={
            <Button
              appearance="subtle"
              size="small"
              icon={React.createElement(ChevronDownRegular, {
                style: { transform: showAdvanced ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.15s' },
              })}
              onClick={() => setShowAdvanced((v) => !v)}
            >
              {showAdvanced ? 'Hide' : 'Show'}
            </Button>
          }
        />
        {showAdvanced && (
          <CardPreview style={{ padding: tokens.spacingVerticalL }}>
            <div className={styles.headerFields}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: tokens.spacingHorizontalM }}>
                <Field label="Webhook URL" hint="POST'd on response.submitted and response.updated events">
                  <Input
                    value={webhookUrl}
                    onChange={(_, d) => updateTemplate({ webhookUrl: d.value || undefined })}
                    placeholder="https://your-crm.example.com/webhook"
                    disabled={disabled}
                    type="url"
                  />
                </Field>
                <Field label="Notification Email" hint="Email address to notify on new submissions">
                  <Input
                    value={notifyEmail}
                    onChange={(_, d) => updateTemplate({ notifyEmail: d.value || undefined })}
                    placeholder="team@example.com"
                    disabled={disabled}
                    type="email"
                  />
                </Field>
              </div>
              <div className={styles.settingsRow}>
                <Field label="Response Editing">
                  <select
                    value={responseEditMode}
                    onChange={(e) => updateTemplate({ responseEditMode: e.target.value as 'readonly' | 'editable' })}
                    disabled={disabled}
                    style={nativeSelectStyle}
                  >
                    <option value="readonly">Read-only after submit</option>
                    <option value="editable">Allow edits after submit</option>
                  </select>
                </Field>
                <Switch
                  checked={showResponseUrl}
                  onChange={(_, d) => updateTemplate({ showResponseUrl: d.checked })}
                  label="Show respondent a link to their response"
                  disabled={disabled}
                />
              </div>

              <Divider style={{ margin: `${tokens.spacingVerticalM} 0` }} />

              <Field
                label="External ID Parameter"
                hint='URL param key used to identify this record in your external system (e.g. "contactId", "assetId")'
              >
                <Input
                  value={externalIdParam}
                  onChange={(_, d) => updateTemplate({ externalIdParam: d.value || undefined })}
                  placeholder="contactId"
                  disabled={disabled}
                  style={{ maxWidth: '240px' }}
                />
              </Field>

              <div>
                <Text size={200} weight="semibold">Field Mappings</Text>
                <Text size={200} style={{ color: tokens.colorNeutralForeground3, display: 'block', marginBottom: tokens.spacingVerticalXS }}>
                  Map your external system's field names to form field names. Used to translate inbound prefill data and outbound webhook payloads.
                </Text>
                <div style={{ display: 'flex', flexDirection: 'column', gap: tokens.spacingVerticalXS }}>
                  {fieldMappings.map((mapping, idx) => (
                    <div key={idx} style={{ display: 'flex', gap: tokens.spacingHorizontalXS, alignItems: 'center' }}>
                      <Field label={idx === 0 ? 'External field' : undefined} style={{ flex: 1 }}>
                        <Input
                          value={mapping.externalField}
                          onChange={(_, d) => {
                            const updated = fieldMappings.map((m, i) => i === idx ? { ...m, externalField: d.value } : m);
                            updateTemplate({ fieldMappings: updated });
                          }}
                          placeholder="FirstName"
                          disabled={disabled}
                          size="small"
                        />
                      </Field>
                      <Text style={{ marginTop: idx === 0 ? tokens.spacingVerticalL : undefined }}>→</Text>
                      <Field label={idx === 0 ? 'Form field' : undefined} style={{ flex: 1 }}>
                        <select
                          value={mapping.formField}
                          onChange={(e) => {
                            const updated = fieldMappings.map((m, i) => i === idx ? { ...m, formField: e.target.value } : m);
                            updateTemplate({ fieldMappings: updated });
                          }}
                          disabled={disabled}
                          style={{ ...nativeSelectStyle, width: '100%', fontSize: '12px' }}
                        >
                          <option value="">— select field —</option>
                          {fields.filter((f) => f.name).map((f) => (
                            <option key={f.id} value={f.name}>{f.label} ({f.name})</option>
                          ))}
                          <option value={mapping.formField && !fields.find((f) => f.name === mapping.formField) ? mapping.formField : '__custom__'}>
                            Custom write-in...
                          </option>
                        </select>
                        {(mapping.formField === '__custom__' || (mapping.formField && !fields.find((f) => f.name === mapping.formField))) && (
                          <Input
                            value={mapping.formField === '__custom__' ? '' : mapping.formField}
                            onChange={(_, d) => {
                              const updated = fieldMappings.map((m, i) => i === idx ? { ...m, formField: d.value } : m);
                              updateTemplate({ fieldMappings: updated });
                            }}
                            placeholder="formFieldName"
                            disabled={disabled}
                            size="small"
                            style={{ marginTop: tokens.spacingVerticalXS }}
                          />
                        )}
                      </Field>
                      <Button
                        icon={React.createElement(DeleteRegular)}
                        appearance="subtle"
                        size="small"
                        disabled={disabled}
                        style={{ marginTop: idx === 0 ? tokens.spacingVerticalL : undefined }}
                        onClick={() => updateTemplate({ fieldMappings: fieldMappings.filter((_, i) => i !== idx) })}
                      />
                    </div>
                  ))}
                  <Button
                    icon={React.createElement(AddRegular)}
                    appearance="subtle"
                    size="small"
                    disabled={disabled}
                    onClick={() => updateTemplate({ fieldMappings: [...fieldMappings, { externalField: '', formField: '' }] })}
                    style={{ alignSelf: 'flex-start', marginTop: tokens.spacingVerticalXS }}
                  >
                    Add Mapping
                  </Button>
                </div>
              </div>
            </div>
          </CardPreview>
        )}
      </Card>

      {showActions && (
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: tokens.spacingHorizontalM }}>
          {onCancel && (
            <Button appearance="secondary" onClick={onCancel} disabled={isSaving}>
              Cancel
            </Button>
          )}
          {onSave && (
            <Button
              appearance="primary"
              onClick={onSave}
              disabled={disabled || isSaving || !title}
            >
              {isSaving ? 'Saving...' : 'Save Request'}
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

export default FormBuilder;
